import type {
  CatalogSeries,
  CodeExplanation,
  Product,
  SupplierFormOptions,
  SupplierOption,
  Unit4CompositionLine,
  Unit4Product,
  Unit4Status,
} from '@klingerator/shared';
import { api } from './api';

export interface Unit4SearchParams {
  /** Zoekt op productcode (BEGINS) */
  code?: string;
  /** Zoekt op omschrijving */
  q?: string;
  limit?: number;
}

export interface Unit4LoadResult {
  product: Product;
  /** id's van gematerialiseerde (sub)producten */
  materialized: number[];
}

/** Typed wrappers rond de Unit4-endpoints (`/api/unit4/*`). */
export const unit4Service = {
  async suppliers(q: string): Promise<SupplierOption[]> {
    const { data } = await api.get<{ items: SupplierOption[] }>('/unit4/suppliers', {
      params: { q },
      silent: true,
    });
    return data.items;
  },

  async supplierOptions(): Promise<SupplierFormOptions> {
    const { data } = await api.get<SupplierFormOptions>('/unit4/supplier-options', { silent: true });
    return data;
  },

  async search(
    params: Unit4SearchParams,
    opts: { silent?: boolean; noToast?: boolean; signal?: AbortSignal } = {},
  ): Promise<Unit4Product[]> {
    return (await this.searchWithMeta(params, opts)).items;
  },

  /** Zoeken incl. bron: `cache` = lokale catalogus (vrij zoeken), `unit4` = live (code/fallback). */
  async searchWithMeta(
    params: Unit4SearchParams,
    opts: { silent?: boolean; noToast?: boolean; signal?: AbortSignal } = {},
  ): Promise<{ items: Unit4Product[]; source: 'cache' | 'unit4'; synced_at: string | null }> {
    const { data } = await api.get<{
      items: Unit4Product[];
      source?: 'cache' | 'unit4';
      synced_at?: string | null;
    }>('/unit4/products', {
      params: { limit: 25, ...params },
      ...opts,
    });
    return { items: data.items, source: data.source ?? 'unit4', synced_at: data.synced_at ?? null };
  },

  /** Reeks-analyse voor de productcode-samensteller (uitleg, volgende posities, volgend vrij nummer). */
  async codeSeries(prefix: string, opts: { signal?: AbortSignal } = {}): Promise<CatalogSeries> {
    const { data } = await api.get<CatalogSeries>('/unit4/catalog/series', {
      params: { prefix },
      silent: true,
      noToast: true,
      ...opts,
    });
    return data;
  },

  /** Uitleg van een (deel van een) productcode volgens de Klinger-codesleutel. */
  async explainCode(code: string, opts: { signal?: AbortSignal } = {}): Promise<CodeExplanation> {
    const { data } = await api.get<CodeExplanation>('/unit4/catalog/explain', {
      params: { code },
      silent: true,
      noToast: true,
      ...opts,
    });
    return data;
  },

  async get(code: string, unit?: string | null, opts: { noToast?: boolean } = {}): Promise<Unit4Product> {
    const { data } = await api.get<Unit4Product>(`/unit4/products/${encodeURIComponent(code)}`, {
      params: unit ? { unit } : undefined,
      ...opts,
    });
    return data;
  },

  async compositions(code: string, opts: { noToast?: boolean } = {}): Promise<Unit4CompositionLine[]> {
    const { data } = await api.get<{ items: Unit4CompositionLine[] }>(
      `/unit4/products/${encodeURIComponent(code)}/compositions`,
      opts,
    );
    return data.items;
  },

  /** Laadt/materialiseert een Unit4-product (incl. compositie) als lokaal product. */
  async load(product_code: string, unit_code?: string | null): Promise<Unit4LoadResult> {
    const { data } = await api.post<Unit4LoadResult>('/unit4/load', {
      product_code,
      unit_code: unit_code || undefined,
    });
    return data;
  },

  async status(): Promise<Unit4Status> {
    const { data } = await api.get<Unit4Status>('/settings/unit4-status', { silent: true, noToast: true });
    return data;
  },
};
